import {
  DropdownButtonType,
  FilteringListProps,
  ListItem,
  SingleSelectDropdown
} from '@tableau/widgets';
import React from 'react';

import { useIsMounted } from '~src/bootstrap/hooks/useIsMounted';
import { Messages } from '~src/codegen/Localize';
import { TypeUnsafeTranslation } from '~src/commonInfra/services/IReactTranslateService';
import { FieldValuesQuery } from '~src/httpClient/resources/FieldValuesQuery';
import { IFieldValue } from '~src/httpClient/serverApi/types/IFieldValue';
import { ITypeaheadFilterDefinition } from '~src/spacesPlatform/services/FilterPanelService';

import { ClearFilterButton } from './ClearFilterButton';
import { FilteringListWithSpinner } from './FilteringListWithSpinner';
import { IFilterControl, submitUrlQuery } from './FilterPanel';
import {
  dropdownListContainer,
  filterComponent,
  filteringListContainerHeight,
  filterWrapper
} from './FilterPanelStyles';
import { ListItemContentWithCount } from './ListItemContentWithCount';

const INITIAL_SLICE_SIZE = 50;
const LIST_ITEM_HEIGHT = 20;
const SCROLL_SLICE_SIZE = Math.ceil(filteringListContainerHeight / LIST_ITEM_HEIGHT);

export interface TypeaheadDropdownFilterProps {
  readonly filterControl: IFilterControl; // Expect to have definition of type ITypeaheadFilterDefinition
  readonly getFieldValueQuery: (filterField: string) => FieldValuesQuery;
  readonly shouldClear: boolean;
}

export function TypeaheadDropdownFilter(props: TypeaheadDropdownFilterProps) {
  const { filterControl } = props;
  const field = (filterControl.definition as ITypeaheadFilterDefinition).field;

  const isMounted = useIsMounted();

  const [selectedItem, setSelectedItem] = React.useState<IFieldValue>((filterControl.value as IFieldValue));
  const dropdownRef = React.useRef<SingleSelectDropdown>();
  const buttonRef = React.useRef<HTMLButtonElement>();

  const [fetchNextPage, setFetchNextPage] = React.useState<boolean>(false);
  const [fieldValues, setFieldValues] = React.useState<IFieldValue[]>([]);
  const [listItems, setListItems] = React.useState<ListItem[]>([]);
  const [localResultCount, setLocalResultCount] = React.useState<number>(0);
  const [remoteResultCount, setRemoteResultCount] = React.useState<number>(0);
  const [searchText, setSearchText] = React.useState<string>('');
  const [isBusy, setIsBusy] = React.useState<boolean>(false);

  const listProps = (): FilteringListProps => {
    const sharedTestId = `filter-${filterControl.definition.name}`;
    return {
      items: listItems,
      autoFocus: true,
      listTestId: filterControl.definition.name + '-filtered-list',
      inputTestId: sharedTestId,
      testId: sharedTestId,
      onFilterChange: (value: string) => setSearchText(value),
      searchPlaceholder: Messages.search(),
      searchText: searchText,
    };
  };

  const buildListItem = (item: IFieldValue): ListItem => {
    return {
      content: <ListItemContentWithCount label={item.label} count={item.count} />,
      value: item.value,
      testId: 'list-item-' + item.value,
    };
  };

  const fetchInitial = async () => {
    const listResults =
      await props.getFieldValueQuery(field)
        .withMatchText(searchText)
        .getSlice(0, INITIAL_SLICE_SIZE);
    const newItems = listResults.items.map((item: IFieldValue) => buildListItem(item));
    setRemoteResultCount(listResults.total);
    setLocalResultCount(listResults.items.length);
    setFieldValues(listResults.items);
    setListItems(newItems);
  };

  const fetchNext = async () => {
    const listResults =
      await props.getFieldValueQuery(field)
        .withMatchText(searchText)
        .getSlice(localResultCount, SCROLL_SLICE_SIZE);
    const newItems = listResults.items.map((item: IFieldValue) => buildListItem(item));
    setLocalResultCount(previous => previous + listResults.items.length);
    setFieldValues(previous => previous.concat(listResults.items));
    setListItems(previous => previous.concat(newItems));
    setFetchNextPage(false);
  };

  // When search text changes, fetch list values
  React.useEffect(
    () => {
      if (isMounted)
        fetchInitial();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText]
  );

  React.useEffect(
    () => {
      if (isMounted && fetchNextPage && localResultCount < remoteResultCount) {
        fetchNext();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchNextPage]
  );

  // Needed for updates from the URL
  React.useEffect(
    () => {
      const updatedValue = filterControl.value;
      if (selectedItem !== updatedValue) {
        setSelectedItem(updatedValue);
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterControl.value]
  );

  // Listen for clearing all filters
  React.useEffect(() => {
    if (props.shouldClear) {
      setSelectedItem(undefined);
    }
  }, [props.shouldClear]);

  const handleChange = (selection: string) => {
    const fieldValue: IFieldValue = fieldValues.find(item => item.value === selection);
    setSelectedItem(fieldValue);

    const params = filterControl.definition.valueToParams(fieldValue);
    submitUrlQuery(params);

    dropdownRef.current?.toggle(false);
  };

  const clearFilter = () => {
    handleChange(undefined);
  };

  return <div className={filterWrapper}>
    <SingleSelectDropdown
      buttonRef={buttonRef}
      children={selectedItem?.label ?? ''}
      className={filterComponent}
      label={TypeUnsafeTranslation.translate(filterControl.definition.name)}
      list={<FilteringListWithSpinner
        className={dropdownListContainer}
        onScroll={() => setFetchNextPage(true)}
        showSpinner={isBusy}
        {...listProps()}
      />}
      onChange={handleChange}
      onClose={() => {
        setFetchNextPage(false);
        setFieldValues([]);
        setListItems([]);
        setLocalResultCount(0);
        setRemoteResultCount(0);
        setSearchText('');
      }}
      onOpen={() => {
        setIsBusy(true);
        fetchInitial().then(() => {
          setIsBusy(false);
        });
      }}
      ref={dropdownRef}
      testId={'typeahead-filter-' + filterControl.definition.name}
      type={DropdownButtonType.Line}
      value={selectedItem?.value?.toString() ?? ''} // bug with interpretting the value as an int for the value vs string for the selectedValue.
    />
    {selectedItem && <ClearFilterButton
      clearFilter={clearFilter}
      filterName={filterControl.definition.name}
      getElementToRefocus={() => buttonRef.current}
    />}
  </div>;
}
